import { Component, Inject } from '@angular/core';
import { Location } from '@angular/common';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environment';
import { emsCompetition, emsMatchMeta, emsTeam, emsVeto } from 'src/models/emsModels';
import { NotificationBar } from 'src/services/notification.service';
import { ConfirmDialogDialog } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-stage-control-panel',
  templateUrl: './stage-control-panel.component.html',
  styleUrl: './stage-control-panel.component.scss'
})
export class StageControlPanelComponent {

  notificationBar: NotificationBar;
  workspaceID = "";
  selectedDate = "";
  competitions = Array<emsCompetition>();
  teams = Array<emsTeam>();
  vetos = Array<emsVeto>(); 
  matches: FormGroup[] = [];
  selectedIndex = 0;
  isDeleteMatch = false;
  placeholderImage = environment.placeholderImage;
  placeholderTeamLogo = environment.placeholderTeamLogo;
  matchesDBRef: any;
  competitionsDBRef: any;
  teamsDBRef: any;
  vetosDBRef: any;

  constructor(private location: Location, private snackBar: MatSnackBar, public dialog: MatDialog, private db: AngularFireDatabase, @Inject(MAT_DIALOG_DATA) public data: any) {
    this.notificationBar = new NotificationBar(snackBar);
  }

  ngOnInit() {
    this.workspaceID  = this.location.path().split("/")[1];
    this.selectedDate = this.data.selectedDate;
    this.selectedIndex = this.data.matchIndex;
    this.loadCompetitions();
    this.loadTeams();
    this.loadVetos();
    this.loadMatches();
  }

  ngOnDestroy() {
    if (this.matchesDBRef) {
      this.matchesDBRef.off('child_added');
      this.matchesDBRef.off('child_changed');
      this.matchesDBRef.off('child_removed');
    };
    if (this.competitionsDBRef) {
      this.competitionsDBRef.off('value');
    };
    if (this.teamsDBRef) {
      this.teamsDBRef.off('value');
    };
    if (this.vetosDBRef) {
      this.vetosDBRef.off('value');
    };
  }

  createMatch() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate + '/' + this.matches.length).update({
      competition: "",
    });
    this.selectedIndex = this.matches.length;
  }

  createMatchFormGroup(matchMeta?: emsMatchMeta): FormGroup {
    return new FormGroup({
      key: new FormControl(''),
      competitionControl: new FormControl(matchMeta?.competition ?? '', Validators.required),
      teamAControl: new FormControl(matchMeta?.teamA ?? '', Validators.required),
      teamBControl: new FormControl(matchMeta?.teamB ?? '', Validators.required),
      vetoControl: new FormControl(matchMeta?.veto ?? '')
    });
  }

  async loadMatches() {
    this.matchesDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate);
    await this.matchesDBRef.once('value', x => {
      this.matches = new Array<FormGroup>();
      x.forEach(match => {  
        var matchMeta = this.createMatchFormGroup(match.toJSON() as emsMatchMeta);
        matchMeta.controls['key'].setValue(match.key);
        this.matches.push(matchMeta);
      });
    });
    this.matchesDBRef.on('child_added', x => {
      if (this.matches.find(match => match.controls['key'].value == x.key)) {
        return;
      }
      var matchMeta = this.createMatchFormGroup(x.toJSON() as emsMatchMeta);
      matchMeta.controls['key'].setValue(x.key);
      this.matches.push(matchMeta);
    });
    this.matchesDBRef.on('child_changed', x => {
      var matchMeta = this.matches.find(match => match.controls['key'].value == x.key);
      if (!matchMeta) { 
        return;
      }
      var newValues = this.createMatchFormGroup(x.toJSON() as emsMatchMeta);
      matchMeta.controls['key'].setValue(x.key);
      matchMeta.controls['competitionControl'].setValue(newValues.controls['competitionControl'].value);
      matchMeta.controls['teamAControl'].setValue(newValues.controls['teamAControl'].value);
      matchMeta.controls['teamBControl'].setValue(newValues.controls['teamBControl'].value);
      matchMeta.controls['vetoControl'].setValue(newValues.controls['vetoControl'].value);
    });
    this.matchesDBRef.on('child_removed', x => {
      this.matches = this.matches.filter(match => match.controls['key'].value != x.key);
    });
  }

  loadCompetitions() {
    this.competitionsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions').orderByChild('workspace').equalTo(this.workspaceID);
    this.competitionsDBRef.on('value', x => {
      this.competitions = new Array<emsCompetition>();
      x.forEach( x => {
        var competition = x.toJSON() as emsCompetition;
        competition.uid = x.key;
        if (competition.active == true) {
          this.competitions.push(competition);
        }
      });
      this.competitions.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  getSelectedCompetition(uid: string) {
    return this.competitions.find(competition => competition.uid == uid);
  }

  loadTeams() {
    this.teamsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/teams').orderByChild('workspace').equalTo(this.workspaceID);
    this.teamsDBRef.on('value', x => {
      this.teams = new Array<emsTeam>();
      x.forEach( x => {
        var team = x.toJSON() as emsTeam;
        team.uid = x.key;
        this.teams.push(team);
      });
      this.teams.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  getSelectedTeam(uid: string) {
    return this.teams.find(team => team.uid == uid);
  }

  getTeamTricode(teamUID: string) {
    return this.teams.find(x => x.uid == teamUID).tricode;
  }

  loadVetos() {
    this.vetosDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/vetos/lobbies').orderByChild('workspace').equalTo(this.workspaceID);
    this.vetosDBRef.on('value', x => {
      var rawData = x.toJSON();
      if (rawData) {
        var vetos = Object.keys(rawData).map(key => ({
          uid: key,
          ...rawData[key],
          actions: Object.values(rawData[key]?.actions??{}),
          pool: Object.values(rawData[key]?.pool??{})
        })) as emsVeto[];
        this.vetos = vetos;
        this.vetos.sort((a, b) => b.date.localeCompare(a.date));
      }
      else {
        this.vetos = new Array<emsVeto>();
      }
    });
  }

  changeCompetition(matchIndex: number) {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate + '/' + matchIndex).update({
      competition: this.matches[matchIndex].controls['competitionControl'].value,
      teamA: null,
      teamB: null,
      veto: null,
    });
  }

  changeTeamA(matchIndex: number) { 
    this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate + '/' + matchIndex).update({
      teamA: this.matches[matchIndex].controls['teamAControl'].value
    });
  }

  changeTeamB(matchIndex: number) { 
    this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate + '/' + matchIndex).update({
      teamB: this.matches[matchIndex].controls['teamBControl'].value
    });
  }

  changeVeto(matchIndex: number) { 
    if (this.matches[matchIndex].controls['vetoControl'].value != "") {
      this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate + '/' + matchIndex).update({
        veto: this.matches[matchIndex].controls['vetoControl'].value
      });
    }
    else {
      this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate  + '/' + matchIndex + '/veto').remove();
    }
  }

  deleteMatch(matchIndex: number) {
    this.isDeleteMatch = true;
    var dialogRef = this.dialog.open(ConfirmDialogDialog, {
      data: {
        actionType: "delete",
        message: "Are you sure you want to delete the " + (matchIndex + 1 ) + ".  match?",
      }
    });
    return dialogRef.afterClosed().toPromise().then(async res => {
      if (res == true) {
        var matchesToRestore = this.matches;
        this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate).remove();
        matchesToRestore.splice(matchIndex, 1);
        var currentMatch = 0;
        matchesToRestore.forEach(match => {
          this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate + '/' + currentMatch).update({
            competition: match.controls['competitionControl'].value,
            teamA: match.controls['teamAControl'].value,
            teamB: match.controls['teamBControl'].value,
            veto: match.controls['vetoControl'].value,
          });
          currentMatch++;
        })
        this.notificationBar.show("Match deleted!");
      };
      this.isDeleteMatch = false;
    });
  }
}